/**
 * Volunteers (1.1.0) — Companion & Wellness and Reading & Services. The vocabulary, the rules and the
 * input schemas shared by the app and the API: statuses, services, the Ready checklist, availability and
 * its plain-words sentence, request categories with their VISIONS codes, walking directions and the
 * approved-area check, outings and the 1:2 ratio, the five completion questions and the incident report.
 * REQUIREMENTS.md §5; docs/DESIGN-DECISIONS-v4.md (Volunteers).
 */
import { z } from 'zod';
import { addDaysToKey, dayOfWeek, isDateKey, keyToIsoDay } from './dates';

/* ------------------------------------------------------------------------------------------------
 * Statuses and services
 * ---------------------------------------------------------------------------------------------- */

/** Eli, 15 Oct 2026: 'review' — signed up on the open link (the QR code); nothing is offered until the office approves. */
export const VOLUNTEER_STATUSES = ['review', 'interested', 'signedUp', 'ready', 'inactive', 'optedOut'] as const;
export type VolunteerStatus = (typeof VOLUNTEER_STATUSES)[number];

/**
 * How a status is written to SM_Volunteers.VolunteerStatus (the live choice values, read 1 Oct 2026 — 1.4.0).
 * 1.5.4: "Opted Out" is the value scripts/add-volunteer-optout-status.ps1 adds; until the site has it the row
 * says OPTED_OUT_FALLBACK and the notes say why (D-1.5.4-2).
 */
export const STATUS_CHOICE: Record<VolunteerStatus, string> = { review: 'Prospective', interested: 'Prospective', signedUp: 'Onboarding', ready: 'Active', inactive: 'Inactive', optedOut: 'Opted Out' };
export const OPTED_OUT_CHOICE = 'Opted Out';
export const OPTED_OUT_FALLBACK = 'Inactive';

/** SM_Volunteers.OnboardingStatus for each status (live choice values). */
export const ONBOARDING_CHOICE: Record<VolunteerStatus, string> = { review: 'In Progress', interested: 'Not Started', signedUp: 'In Progress', ready: 'Complete', inactive: 'Not Started', optedOut: 'Not Started' };

export const SERVICE_KEYS = ['reading', 'companion', 'wellness', 'events', 'lessons', 'other'] as const;
export type ServiceKey = (typeof SERVICE_KEYS)[number];

/**
 * The services' names in words (the card, the export, the emails). The live qualifications list has no
 * service column — it is one row per volunteer and program (SERVICE_PROGRAM), read 1 Oct 2026.
 */
export const SERVICE_QUALIFICATION: Record<ServiceKey, string> = {
  reading: 'Reading',
  companion: 'Companion Support',
  wellness: 'Wellness Walking',
  events: 'Community Outing / Event Support',
  lessons: 'Lessons',
  other: 'Other',
};

/** The services as the card and the audit lines name them (1.7.1: "Reading added by Caroline"). */
export const SERVICE_LABEL_EN: Record<ServiceKey, string> = { reading: 'Reading', companion: 'Companion', wellness: 'Wellness walks', events: 'Events', lessons: 'Lessons', other: 'Other' };

/** The roster's words (import, D-1.0.0-9) → services. "Companion" on the roster = trips and walks. */
export const ROSTER_SERVICES: Record<string, ServiceKey[]> = {
  Reading: ['reading'],
  Companion: ['companion', 'wellness'],
  'Event Support': ['events'],
  Lessons: ['lessons'],
  Other: ['other'],
};

export function servicesFromRoster(words: readonly string[]): ServiceKey[] {
  const out = new Set<ServiceKey>();
  for (const w of words) for (const s of ROSTER_SERVICES[w] ?? []) out.add(s);
  return SERVICE_KEYS.filter((s) => out.has(s));
}

/**
 * Which program a service belongs to (SM_Programs.ProgramCode, the five codes on the live site, read
 * 1 Oct 2026). Wellness walks and outings fall back to COMPANION where a site has no row for their own code.
 */
export type VolunteerProgramCode = 'READING' | 'COMPANION' | 'WELLNESS_WALK' | 'COMMUNITY_OUTING';
export const SERVICE_PROGRAM: Record<ServiceKey, VolunteerProgramCode> = {
  reading: 'READING',
  lessons: 'READING',
  other: 'READING',
  companion: 'COMPANION',
  wellness: 'WELLNESS_WALK',
  events: 'COMMUNITY_OUTING',
};
export const PROGRAM_FALLBACK: Partial<Record<VolunteerProgramCode, VolunteerProgramCode>> = { WELLNESS_WALK: 'COMPANION', COMMUNITY_OUTING: 'COMPANION' };

/* ------------------------------------------------------------------------------------------------
 * Ready (1.7.1, D-1.7.1-0 — Elijah, 8 Oct 2026): everybody signs the volunteer waiver; Companion & Wellness
 * also needs the training and the background check. The vest and 18+ are notes on the card, never a gate.
 * ---------------------------------------------------------------------------------------------- */

/** Every dated tick a volunteer's file may carry (the vest and 18+ are kept as notes). */
export const TICK_ITEMS = ['age18', 'background', 'training', 'waiver', 'vest'] as const;
export type ReadyItem = (typeof TICK_ITEMS)[number];

/** What Ready is made of, in the order the card and the lines say it. */
export const READY_ITEMS: readonly ReadyItem[] = ['waiver', 'training', 'background'];

/** Set by staff on the card (dated ticks); the waiver tick comes from the signing or a filed paper. */
export const STAFF_TICKS: readonly ReadyItem[] = ['background', 'training'];

/** The services that take a volunteer out with a tenant: the training and the background check apply. */
export const COMPANION_SERVICES: readonly ServiceKey[] = ['companion', 'wellness'];
const isCompanion = (s: ServiceKey) => COMPANION_SERVICES.includes(s);

/** Reading, Lessons, Events, Other: the waiver. Companion and Wellness: the waiver, the training, the background check. */
export function readyItemsFor(services: readonly ServiceKey[]): ReadyItem[] {
  return services.some(isCompanion) ? [...READY_ITEMS] : ['waiver'];
}

/** What a missing item stops: the waiver stops everything; the training and the check stop Companion & Wellness only. */
export function blocksOf(item: ReadyItem): 'all' | 'companion' {
  return item === 'waiver' ? 'all' : 'companion';
}

export interface Tick {
  /** yyyy-mm-dd */
  on: string;
  by: string;
  at: string;
  how?: string;
}

export interface StatusFacts {
  inactive: boolean;
  /** 1.5.4: "Stop my volunteer emails" (their link) or Opt out on the card — no email of any kind from then on. */
  optedOut?: boolean;
  /** 1.5.4: back after opting out — Not ready until the office has re-checked the waiver ("Waiver checked"). */
  recheck?: boolean;
  /** Eli, 15 Oct 2026: signed up on the open link and not yet approved by the office — never offered anything. */
  review?: boolean;
  signedUpAt: string | null;
  services: readonly ServiceKey[];
  /** 1.8.3: how many services the office added (shared/volunteer-services.ts) are ticked — each needs the waiver only. */
  addedCount?: number;
  ready: Partial<Record<ReadyItem, Tick>>;
}

export function missingReady(v: Pick<StatusFacts, 'services' | 'ready'>): ReadyItem[] {
  return readyItemsFor(v.services).filter((i) => !v.ready[i]);
}

/** What one service still needs. */
export function missingFor(service: ServiceKey, ready: StatusFacts['ready']): ReadyItem[] {
  return readyItemsFor([service]).filter((i) => !ready[i]);
}

/**
 * May this volunteer be offered this service? Ticked for it, nothing it needs missing, and not inactive, opted
 * out or waiting for the waiver re-check. A reader who also ticked Companion and lacks the check still reads.
 */
export function readyForService(v: StatusFacts, service: ServiceKey): boolean {
  if (v.optedOut || v.inactive || v.recheck || v.review) return false;
  return v.services.includes(service) && missingFor(service, v.ready).length === 0;
}

/** The programs something is still missing for (the card's "Cannot start …" line names only these). */
export function blockedPrograms(v: Pick<StatusFacts, 'services' | 'ready'>): ('companion' | 'reading')[] {
  const out: ('companion' | 'reading')[] = [];
  if (v.services.some((s) => isCompanion(s) && missingFor(s, v.ready).length)) out.push('companion');
  if (v.services.some((s) => !isCompanion(s) && missingFor(s, v.ready).length)) out.push('reading');
  return out;
}

/** "missing: background check (2), waiver (1)" — how many of a list lack each item, the most common first. */
export function missingCounts(list: readonly { missing: readonly string[] }[]): { item: string; count: number }[] {
  const n = new Map<string, number>();
  for (const v of list) for (const i of v.missing) n.set(i, (n.get(i) ?? 0) + 1);
  const order = ['background', 'training', 'waiver', 'recheck'];
  return [...n.entries()].map(([item, count]) => ({ item, count })).sort((x, y) => y.count - x.count || order.indexOf(x.item) - order.indexOf(y.item));
}

/**
 * Interested (a name, nothing chosen) → Signed up (a service ticked, or the sign-up or the roster said so) →
 * Ready (everything their services need) → Inactive (staff).
 */
export function statusOf(v: StatusFacts): VolunteerStatus {
  if (v.optedOut) return 'optedOut';
  if (v.inactive) return 'inactive';
  if (v.review) return 'review';
  // 1.8.3: a service the office added counts as a service ticked (its Ready is the waiver, as missingReady says for none of the Companion two)
  const ticked = v.services.length + (v.addedCount ?? 0);
  if (!v.signedUpAt && !ticked) return 'interested';
  if (!ticked) return 'signedUp';
  return missingReady(v).length || v.recheck ? 'signedUp' : 'ready';
}

/* ------------------------------------------------------------------------------------------------
 * Availability — weekly chips (Mon–Sun × morning/afternoon/evening) plus single dates on and off
 * ---------------------------------------------------------------------------------------------- */

export const PARTS = ['am', 'pm', 'eve'] as const;
export type Part = (typeof PARTS)[number];

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const AvailabilitySchema = z.object({
  /** Index 0 = Monday … 6 = Sunday. */
  weekly: z.array(z.array(z.enum(PARTS)).max(3)).length(7),
  on: z.array(isoDay).max(120),
  off: z.array(isoDay).max(120),
});
export type Availability = z.infer<typeof AvailabilitySchema>;

export const emptyAvailability = (): Availability => ({ weekly: [[], [], [], [], [], [], []], on: [], off: [] });

/** Normalises: parts in order, no duplicates, a date is never both on and off (off wins). */
export function normalizeAvailability(a: Availability): Availability {
  const off = [...new Set(a.off)].sort();
  return {
    weekly: a.weekly.map((d) => PARTS.filter((p) => d.includes(p))),
    on: [...new Set(a.on)].filter((d) => !off.includes(d)).sort(),
    off,
  };
}

/** Morning before noon, afternoon noon–5 pm, evening from 5 pm. */
export function partOfMinutes(minutes: number): Part {
  return minutes < 12 * 60 ? 'am' : minutes < 17 * 60 ? 'pm' : 'eve';
}

/** Monday = 0 … Sunday = 6 (dayOfWeek gives Sunday = 0). */
export const mondayIndex = (dayKey: string) => (dayOfWeek(dayKey) + 6) % 7;

/** Does this availability fit a visit on `dayKey` at `minutes`? A "can't" date always wins. */
export function fits(a: Availability, dayKey: string, minutes: number): boolean {
  const iso = keyToIsoDay(dayKey);
  if (a.off.includes(iso)) return false;
  if (a.on.includes(iso)) return true;
  return (a.weekly[mondayIndex(dayKey)] ?? []).includes(partOfMinutes(minutes));
}

const DAY_NAMES = {
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  es: ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'],
} as const;
const PART_NAMES = { en: { am: 'morning', pm: 'afternoon', eve: 'evening' }, es: { am: 'mañana', pm: 'tarde', eve: 'noche' } } as const;
const MONTHS = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
} as const;

export function shortDate(iso: string, lang: 'en' | 'es'): string {
  const [, m, d] = iso.split('-').map(Number) as [number, number, number];
  return lang === 'es' ? `${d} ${MONTHS.es[m - 1]}` : `${MONTHS.en[m - 1]} ${d}`;
}

/** "You're available every Thursday afternoon and every Saturday morning, plus Oct 12 and Oct 19, not Oct 23." */
export function availabilitySentence(a: Availability, lang: 'en' | 'es', who: 'you' | 'they' = 'you'): string {
  const es = lang === 'es';
  const parts: string[] = [];
  a.weekly.forEach((ps, i) => {
    if (!ps.length) return;
    const day = DAY_NAMES[lang][i]!;
    const p = ps.length === 3 ? (es ? 'todo el día' : 'all day') : ps.map((x) => PART_NAMES[lang][x]).join(es ? ' y ' : ' and ');
    parts.push(es ? `cada ${day} (${p})` : `every ${day} ${p}`);
  });
  const lead = es ? (who === 'you' ? 'Estás disponible ' : 'Disponible ') : who === 'you' ? "You're available " : 'Available ';
  let s: string;
  if (parts.length) s = lead + parts.join(es ? ' y ' : ' and ');
  else if (a.on.length) s = lead.trimEnd();
  else s = es ? 'Todavía no hay ningún día marcado' : 'No days marked yet';
  if (a.on.length) s += (parts.length ? (es ? ', más ' : ', plus ') : ' ') + a.on.map((d) => shortDate(d, lang)).join(es ? ' y ' : ' and ');
  if (a.off.length) s += (es ? ', no el ' : ', not ') + a.off.map((d) => shortDate(d, lang)).join(es ? ' ni el ' : ' or ');
  return s + '.';
}

/** Days the availability calendar shows: from the Monday of this week, `weeks` weeks (4–8). */
export function calendarDays(todayKey: string, weeks = 8): string[] {
  const start = addDaysToKey(todayKey, -mondayIndex(todayKey));
  return Array.from({ length: weeks * 7 }, (_, i) => addDaysToKey(start, i));
}

/* ------------------------------------------------------------------------------------------------
 * Walking directions and the approved area (no lookup service — plain URLs)
 * ---------------------------------------------------------------------------------------------- */

export const ORIGIN_ADDRESS = '135 W 23rd St, New York, NY 10011';

/** Form-style encoding (spaces as +), as the approved-places seed's links were built. */
const enc = (s: string) =>
  encodeURIComponent(s)
    .replace(/[!'()*]/g, (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase())
    .replace(/%20/g, '+');

export function directionsLinks(address: string, from: string = ORIGIN_ADDRESS): { google: string; apple: string } {
  const full = (a: string) => (/new york|, ny\b/i.test(a) ? a : `${a}, New York, NY`);
  const dest = full(address);
  const origin = full(from);
  return {
    google: `https://www.google.com/maps/dir/?api=1&origin=${enc(origin)}&destination=${enc(dest)}&travelmode=walking`,
    apple: `https://maps.apple.com/?saddr=${enc(origin)}&daddr=${enc(dest)}&dirflg=w`,
  };
}

/** D-1028-COMPANION part 2: a Companion & Wellness request may have up to four stops, in order. */
export const MAX_STOPS = 4;

/**
 * Per-stop walking directions in order: stop 1 from the building, each next stop from the one before it (a stop with
 * no address has no link, and the next one then starts from the last address that was known).
 */
export function stopDirections(stops: readonly { address: string }[]): ({ google: string; apple: string } | null)[] {
  let from: string = ORIGIN_ADDRESS;
  return stops.map((s) => {
    if (!s.address) return null;
    const d = directionsLinks(s.address, from);
    from = s.address;
    return d;
  });
}

/** "Whole Foods Market, then CVS Pharmacy, then the library" — the names in order, for emails and cards. */
export function stopsWords(stops: readonly { name: string }[], then = 'then'): string {
  return stops.map((s) => s.name).join(`, ${then} `);
}

export type AreaVerdict = 'in' | 'out' | 'unknown';

const AVENUE_WORDS: Record<string, number> = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10, eleventh: 11, twelfth: 12,
};

/**
 * The approved area (tenant waiver and handbook): north to 34th St, south to 14th St, west to the High
 * Line, east to Fifth Ave / Lexington at the farthest. Read from the address's streets and avenues; an
 * address the words do not place is "unknown" (the office checks it). Outside = a group outing the office
 * arranges.
 */
export function areaCheck(address: string): AreaVerdict {
  const a = ` ${address} `.replace(/\s+/g, ' ');
  const streets: number[] = [];
  for (const m of a.matchAll(/\b(\d{1,3})(?:st|nd|rd|th)\s+(?:St|Street)\b/gi)) streets.push(Number(m[1]));
  const avenues: number[] = [];
  for (const m of a.matchAll(/\b(\d{1,2})(?:st|nd|rd|th)\s+Ave(?:nue)?\b/gi)) avenues.push(Number(m[1]));
  for (const m of a.matchAll(/\b(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth)\s+Ave(?:nue)?\b/gi)) avenues.push(AVENUE_WORDS[m[1]!.toLowerCase()]!);
  if (/avenue of the americas/i.test(a)) avenues.push(6);
  const eastOfLex = /\b(?:1st|2nd|3rd|first|second|third)\s+Ave|\bYork Ave|\bFDR\b|\bAvenue [A-D]\b/i.test(a);
  const westOfHighLine = avenues.some((n) => n >= 11) || /\b(?:West Side Hwy|Hudson River Park|Chelsea Piers|Pier \d+)/i.test(a);
  if (streets.some((s) => s < 14 || s > 34)) return 'out';
  if (eastOfLex || westOfHighLine) return 'out';
  // "<number> W 23rd St" / "<number> E 23rd St": the block tells the side
  const side = /\b(\d{1,4})\s+(W|E|West|East)\.?\s+\d{1,3}(?:st|nd|rd|th)\b/i.exec(a);
  if (side) {
    const n = Number(side[1]);
    const west = /^w/i.test(side[2]!);
    if (west && n >= 600) return 'out'; // past 11th Ave
    if (!west && n >= 150) return 'out'; // past Lexington
  }
  if (streets.length || /\bHigh Line\b|\bMadison Square\b|\bUnion Square\b|\bFlatiron\b/i.test(a)) return 'in';
  return 'unknown';
}

/* ------------------------------------------------------------------------------------------------
 * Request categories (the big cards) and their VISIONS codes (REQUIREMENTS §12 · docs/VISIONS-EXHIBIT-A.md)
 * ---------------------------------------------------------------------------------------------- */

/**
 * The kinds a request is stored with. 1.8.3 (D-1.8.3-4): `other` is Companion & Wellness → Other — the office types
 * what it is (`otherWhat`, one line). `visit` (Companion visit) stays here because past requests carry it and must
 * read as before; it is no longer offered and the server refuses a new one (RETIRED_CATEGORIES).
 */
export const CATEGORY_KEYS = ['read', 'trip', 'walk', 'visit', 'lesson', 'other'] as const;
export type CategoryKey = (typeof CATEGORY_KEYS)[number];
export const TRIP_KINDS = ['store', 'pharmacy', 'errand'] as const;
export type TripKind = (typeof TRIP_KINDS)[number];

/** "We don't do companion visits" (Elijah, 9 Oct 2026): kept readable on old records, refused on a new request. */
export const RETIRED_CATEGORIES: readonly CategoryKey[] = ['visit'];
/** Other: "Say what it is" — one line. */
export const OTHER_WHAT_MAX = 120;

/**
 * What Book offers for each service, in order (1.8.3). Companion & Wellness asks "What's the plan?": Wellness walk ·
 * Trip to the store · Trip to the pharmacy · Run errands · Other. Reading & Services: Reading · Lessons.
 * `key` is the plan's stable name (the screen's words are `book183.plan.<key>`; the button's test id is `testId`).
 */
export interface RequestPlan {
  key: 'walk' | 'store' | 'pharmacy' | 'errand' | 'other' | 'read' | 'lesson';
  category: CategoryKey;
  trip: TripKind | null;
  testId: string;
}
export const REQUEST_PLANS: Record<'COMPANION' | 'READING', readonly RequestPlan[]> = {
  COMPANION: [
    { key: 'walk', category: 'walk', trip: null, testId: 'req-cat-walk' },
    { key: 'store', category: 'trip', trip: 'store', testId: 'req-trip-store' },
    { key: 'pharmacy', category: 'trip', trip: 'pharmacy', testId: 'req-trip-pharmacy' },
    { key: 'errand', category: 'trip', trip: 'errand', testId: 'req-trip-errand' },
    { key: 'other', category: 'other', trip: null, testId: 'req-cat-other' },
  ],
  READING: [
    { key: 'read', category: 'read', trip: null, testId: 'req-cat-read' },
    { key: 'lesson', category: 'lesson', trip: null, testId: 'req-cat-lesson' },
  ],
};

/** The template's codes (shared/visions.ts DEFAULT_ACTIVITY_CODES); the request cards and outings produce the first seven. */
export const VISIONS_CODES = ['SA', 'PE', 'NE', 'WW', 'GWW', 'SI', 'CE', 'WP', 'REF', 'OTH'] as const;
export type VisionsCode = (typeof VISIONS_CODES)[number];

export interface CategoryDef {
  service: ServiceKey;
  program: 'COMPANION' | 'READING';
  /** Where it happens (SM_Appointments.ServiceLocationType). */
  location: 'Tenant Unit' | 'Community Location';
  /** A destination: required (trip), optional (walk), none (in the building). */
  destination: 'required' | 'optional' | 'none';
  /** The tenant waiver gate applies (Companion & Wellness only). */
  gated: boolean;
}

export const CATEGORIES: Record<CategoryKey, CategoryDef> = {
  read: { service: 'reading', program: 'READING', location: 'Tenant Unit', destination: 'none', gated: false },
  trip: { service: 'companion', program: 'COMPANION', location: 'Community Location', destination: 'required', gated: true },
  walk: { service: 'wellness', program: 'COMPANION', location: 'Community Location', destination: 'optional', gated: true },
  visit: { service: 'companion', program: 'COMPANION', location: 'Tenant Unit', destination: 'none', gated: true },
  lesson: { service: 'lessons', program: 'READING', location: 'Tenant Unit', destination: 'none', gated: false },
  // 1.8.3: Companion & Wellness → Other (the typed words say what) — a place may be named, none is required
  other: { service: 'companion', program: 'COMPANION', location: 'Community Location', destination: 'optional', gated: true },
};

const TRIP_CODE: Record<TripKind, VisionsCode> = { store: 'SA', pharmacy: 'PE', errand: 'NE' };

/** The code that lands on the monthly VISIONS report — Reading and lessons never go on it. */
export function visionsCodeOf(category: CategoryKey, trip?: TripKind | null): VisionsCode | null {
  if (category === 'trip') return TRIP_CODE[trip ?? 'store'];
  if (category === 'walk') return 'WW';
  if (category === 'visit') return 'SI';
  // 1.8.3: behind the scenes only — no Book or request screen prints a code
  if (category === 'other') return 'OTH';
  return null;
}

/**
 * The activity in words for emails ("trip to the store"). Other (1.8.3) reads as the words the office typed, kept
 * exactly as written in either language; with none on the record it reads "other help".
 */
export function activityWords(category: CategoryKey, trip: TripKind | null | undefined, lang: 'en' | 'es', otherWhat?: string | null): string {
  if (category === 'other') return (otherWhat ?? '').trim() || (lang === 'es' ? 'otra ayuda' : 'other help');
  const en: Record<string, string> = { read: 'reading', 'trip:store': 'trip to the store', 'trip:pharmacy': 'trip to the pharmacy', 'trip:errand': 'errand', walk: 'wellness walk', visit: 'companion visit', lesson: 'lesson' };
  const es: Record<string, string> = { read: 'lectura', 'trip:store': 'ida a la tienda', 'trip:pharmacy': 'ida a la farmacia', 'trip:errand': 'diligencia', walk: 'caminata de bienestar', visit: 'visita de compañía', lesson: 'clase' };
  const k = category === 'trip' ? `trip:${trip ?? 'store'}` : category;
  return (lang === 'es' ? es : en)[k] ?? category;
}

/** Eli, 17 Oct 2026: how they get there (asked after the place, one-to-one and group outings). */
export const TRANSPORT_KINDS = ['walking', 'assisted', 'uber', 'bus', 'train'] as const;
export type TransportKind = (typeof TRANSPORT_KINDS)[number];
export const TRANSPORT_EN: Record<TransportKind, string> = { walking: 'Walking', assisted: 'Assisted ride', uber: 'Uber', bus: 'Bus', train: 'Train' };
/**
 * Eli, 17 Oct 2026: links expire once the thing they are for is over; new things get new links. The one link that
 * stays is the volunteer's availability link. After a visit the volunteer still has a week for the sign-out form.
 * 22 Oct 2026 (D-1022-ONBOARD): a registration link ("Complete your registration", "Review and update your
 * information") has no end date — the volunteer may come back to it whenever they like; the office can always resend,
 * and "Turn links off" on the volunteer's page is what makes a newer link necessary.
 */
export const COMPLETION_LINK_DAYS = 7;
/** Where every outing starts (Eli: "we're always starting from 135 West 23rd Street"). */
export const START_ADDRESS = '135 West 23rd Street';

/** One hour door to door, lobby to lobby (the handbook's standard outing). */
export const DEFAULT_DURATION_MINUTES = 60;
export const CONSISTENT_WEEKS = [4, 8, 12] as const;

/* ------------------------------------------------------------------------------------------------
 * Outings — the 1:2 ratio
 * ---------------------------------------------------------------------------------------------- */

/**
 * At least one volunteer for every two tenants: "5 volunteers → up to 10 tenants". A warning, never a stop
 * (1.4.0 §2): a volunteer may take 3–4 when some walk on their own. Tenants only — a guest is never counted.
 */
export const tenantsFor = (volunteers: number) => Math.max(0, Math.floor(volunteers)) * 2;
export function ratioOk(volunteers: number | null, tenants: number | null): boolean {
  if (volunteers === null || tenants === null) return true;
  return tenants <= tenantsFor(volunteers);
}

/* ------------------------------------------------------------------------------------------------
 * Input schemas
 * ---------------------------------------------------------------------------------------------- */

const text = (max: number) => z.string().trim().max(max);
const optText = (max: number) => z.string().trim().max(max).optional().default('');
const email = z.string().trim().toLowerCase().max(254).regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'email');
const optEmail = z.union([z.literal(''), email]).optional().default('');
const personName = z.string().trim().min(1, 'required').max(60);
const phone = z
  .string()
  .trim()
  .max(40)
  .refine((s) => s === '' || s.replace(/\D/g, '').length >= 10, 'phone');
const dayKey = z.string().refine(isDateKey, 'date');
/** Minutes after midnight, 6:00 am to 9:00 pm. */
const minutes = z.number().int().min(6 * 60).max(21 * 60);

export const REACH_BY = ['text', 'email', 'call'] as const;

/** 1.7.9: where they come from — optional, never a gate (shared/volunteer-affiliation.ts). */
const affiliationFields = { affiliation: optText(60), affiliationOther: optText(60), affiliationGroup: optText(120) };

/** 1.8.3: the keys of services the office added (shared/volunteer-services.ts) — checked against the list on the server. */
const addedKeys = z.array(z.string().trim().min(1).max(40)).max(40).optional().default([]);

export const AddVolunteerSchema = z.object({ firstName: personName, lastName: personName, email: optEmail, ...affiliationFields, addedServices: addedKeys });
export type AddVolunteer = z.infer<typeof AddVolunteerSchema>;

/** The seven-screen sign-up (screens 1–5 are answers; 6 is the waiver; 7 is Done). */
export const VolunteerAboutSchema = z.object({
  firstName: personName,
  lastName: personName,
  email: optEmail,
  mobile: phone,
  street: optText(120),
  city: optText(60),
  state: optText(20),
  zip: optText(12),
  reachBy: z.array(z.enum(REACH_BY)).max(3).default([]),
  adult: z.enum(['yes', 'no', '']).default(''),
  languages: optText(120),
  heardFrom: optText(160),
  ...affiliationFields,
});

export const VolunteerHelpSchema = z.object({
  reading: z.boolean().default(false),
  companion: z.boolean().default(false),
  events: z.boolean().default(false),
  lessons: z.boolean().default(false),
  lessonsWhat: optText(120),
  other: z.boolean().default(false),
  otherWhat: optText(120),
  experience: z.enum(['yes', 'no', '']).default(''),
  anythingElse: optText(1000),
  /** 1.8.2 (D-1.8.2-8): "I understand the training and background check." — required with Companion trips and wellness walks. */
  trainingAck: z.boolean().default(false),
  /** 1.8.3 (D-1.8.3-5): the services the office added that they ticked (keys; the waiver is all each needs). */
  added: addedKeys,
});

export const VolunteerEmergencySchema = z.object({ name: optText(80), phone: phone.optional().default(''), relation: optText(40) });

/**
 * 22 Oct 2026 (D-1022-ONBOARD): Add volunteer, the office's side. Everything the office knows when the person applies
 * (the website's form, a phone call) goes on the record at once; every field but the name is optional. With
 * `sendWelcome` the same press queues the welcome email, whose link opens the registration with all of it filled in.
 * `services` are the sign-up form's ticks: `companion` stands for Companion trips and Wellness walks together.
 */
export const AddVolunteerFullSchema = AddVolunteerSchema.extend({
  mobile: phone.optional().default(''),
  street: optText(120),
  city: optText(60),
  state: optText(20),
  zip: optText(12),
  languages: optText(120),
  heardFrom: optText(160),
  services: z.array(z.enum(['reading', 'companion', 'events'])).max(3).optional().default([]),
  availability: AvailabilitySchema.optional(),
  emergency: VolunteerEmergencySchema.optional(),
  sendWelcome: z.boolean().optional().default(false),
});
export type AddVolunteerFull = z.infer<typeof AddVolunteerFullSchema>;

/** 22 Oct 2026: the office edits a volunteer's own details on their page (any volunteer, "I want to volunteer" too). */
export const VolunteerContactSchema = z.object({
  firstName: personName,
  lastName: personName,
  email: optEmail,
  mobile: phone,
  street: optText(120),
  city: optText(60),
  state: optText(20),
  zip: optText(12),
  languages: optText(120),
  heardFrom: optText(160),
  emergency: VolunteerEmergencySchema,
});
export type VolunteerContact = z.infer<typeof VolunteerContactSchema>;

export const VolunteerFormSchema = z.object({
  about: VolunteerAboutSchema,
  help: VolunteerHelpSchema,
  emergency: VolunteerEmergencySchema.default({}),
  availability: AvailabilitySchema.default(emptyAvailability()),
  handbookRead: z.boolean().default(false),
});
export type VolunteerForm = z.infer<typeof VolunteerFormSchema>;
export type VolunteerFormInput = z.input<typeof VolunteerFormSchema>;

/** A draft keeps whatever is typed; nothing is checked until the step's Next. */
export const VolunteerDraftSchema = z.object({ step: z.number().int().min(0).max(7), values: z.unknown() });

export function servicesOfHelp(h: z.infer<typeof VolunteerHelpSchema>): ServiceKey[] {
  const out: ServiceKey[] = [];
  if (h.reading) out.push('reading');
  if (h.companion) out.push('companion', 'wellness');
  if (h.events) out.push('events');
  if (h.lessons) out.push('lessons');
  if (h.other) out.push('other');
  return out;
}

export const needsEmergency = (h: z.infer<typeof VolunteerHelpSchema>) => h.companion || h.events;

/** What stops a step's Next, as i18n keys (the form shows them; the server checks the same on Submit). */
/**
 * `o.website` (Eli, 15 Oct 2026): a sign-up from the open link (the QR code) asks what the website's volunteer form
 * requires — phone, email and the full home address, as well as the name.
 */
export function formProblems(f: VolunteerFormInput, step: number, o: { website?: boolean } = {}): string[] {
  const out: string[] = [];
  const a = VolunteerAboutSchema.safeParse(f.about ?? {});
  if (step >= 1) {
    if (!a.success) for (const i of a.error.issues) out.push(`about.${String(i.path[0])}`);
    else if (a.data.adult !== 'yes') out.push(a.data.adult === 'no' ? 'about.under18' : 'about.adult');
    else if (o.website) {
      for (const k of ['email', 'mobile', 'street', 'city', 'state', 'zip'] as const) if (!a.data[k]) out.push(`about.${k}`);
    } else if (!a.data.mobile && !a.data.email) out.push('about.contact');
  }
  if (step >= 2) {
    const h = VolunteerHelpSchema.safeParse(f.help ?? {});
    if (!h.success || (!servicesOfHelp(h.data).length && !h.data.added.length)) out.push('help.none');
    else {
      if (h.data.lessons && !h.data.lessonsWhat) out.push('help.lessonsWhat');
      if (h.data.other && !h.data.otherWhat) out.push('help.otherWhat');
      if (h.data.companion && !h.data.trainingAck) out.push('help.trainingAck');
    }
  }
  if (step >= 3) {
    // Eli, 26 Oct 2026 (D-1026-EMERGENCY): the emergency contact is optional everywhere; a phone, if given, must be a valid phone
    const e = VolunteerEmergencySchema.safeParse(f.emergency ?? {});
    if (!e.success) out.push('emergency.phone');
  }
  // Eli, 13 Oct 2026: the handbook is sent after sign-up, never a tick the form waits for
  return out;
}

export const WaiverSignSchema = z.object({
  typedName: z.string().trim().min(2).max(120),
  agree: z.literal(true),
  hash: z.string().regex(/^[0-9a-f]{64}$/i),
  lang: z.enum(['en', 'es']).default('en'),
});

export const ReadyTickSchema = z.object({
  item: z.enum(['background', 'training', 'vest', 'age18', 'waiver']),
  on: z.boolean(),
  date: isoDay.optional(),
});

/** 1.7.1: the Services card — the six services as the office ticks them. 1.8.3: and the keys of the services the office added (checked against the list on the server). */
export const ServicesPostSchema = z.array(z.string().trim().min(1).max(40)).max(SERVICE_KEYS.length + 40);

export const VOLUNTEER_DOC_KINDS = ['waiver', 'background', 'training', 'other'] as const;
export type VolunteerDocKind = (typeof VOLUNTEER_DOC_KINDS)[number];
/** Which tick a filed document sets. The background check letter sets none: the office ticks "cleared" with its date (1.7.1). */
export const DOC_TICK: Partial<Record<VolunteerDocKind, ReadyItem>> = { waiver: 'waiver', training: 'training' };
export const DOC_LABEL_EN: Record<VolunteerDocKind, string> = { waiver: 'Waiver', background: 'Background check', training: 'Training certificate', other: 'Document' };

export const VolunteerDocSchema = z.object({
  kind: z.enum(VOLUNTEER_DOC_KINDS),
  label: optText(80),
  /** yyyy-mm-dd the document is dated (defaults to today). */
  date: isoDay.optional(),
  fileName: text(200),
  contentType: z.enum(['application/pdf', 'image/jpeg', 'image/png', 'image/webp']),
  base64: z.string().min(8).max(21_000_000),
});

export const VolunteerClassifySchema = z.object({ name: text(200).min(1), kind: z.enum(VOLUNTEER_DOC_KINDS), label: optText(80), date: isoDay.optional() });

export const DestinationSchema = z.object({
  name: text(120).min(1),
  address: text(200),
  /** From the approved list (true) or typed (false). */
  approved: z.boolean().default(false),
});
export type Destination = z.infer<typeof DestinationSchema>;

/**
 * A guest's first name — nothing else is stored about a guest (1.4.0 §2): letters, a space, an apostrophe, a
 * hyphen or a period; never a number or an address.
 */
export const GuestFirstNameSchema = z
  .string()
  .trim()
  .min(1, 'required')
  .max(40)
  .regex(/^[\p{L}][\p{L}\p{M} '’.-]*$/u, 'firstName');

/** "Is a second person coming?" — No / Another tenant (their own record) / A guest (first name only). */
export const SecondPersonSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('tenant'), tenantId: text(80).min(1) }),
  z.object({ kind: z.literal('guest'), firstName: GuestFirstNameSchema }),
]);
export type SecondPerson = z.infer<typeof SecondPersonSchema>;

export const RequestSchema = z
  .object({
    tenantId: text(80).min(1),
    category: z.enum(CATEGORY_KEYS),
    trip: z.enum(TRIP_KINDS).nullable().optional().default(null),
    /** 1.8.3 — Other: "Say what it is" (one line; required for `other`, ignored for every other kind). */
    otherWhat: z
      .string()
      .trim()
      .max(OTHER_WHAT_MAX)
      .refine((s) => !/[\r\n]/.test(s), 'one-line')
      .optional()
      .default(''),
    dayKey,
    minutes,
    duration: z.number().int().min(15).max(240).default(DEFAULT_DURATION_MINUTES),
    mode: z.enum(['one-time', 'consistent']),
    weeks: z.number().int().min(1).max(12).default(8),
    destination: DestinationSchema.nullable().optional().default(null),
    /** D-1028-COMPANION part 2: stops 2 to 4, in order (stop 1 is `destination`). Each from the approved list or typed. */
    moreStops: z.array(DestinationSchema).max(MAX_STOPS - 1).optional().default([]),
    notes: optText(1000),
    /** "available" = Ready + service + availability; "all" = every Ready volunteer of the service. */
    // Eli, 15 Oct 2026: 'none' — saved without sending; the office picks the volunteer (who said yes by email or phone)
    // Eli, 17 Oct 2026: 'one' — ask one volunteer the office picked (volunteerId) to confirm
    audience: z.enum(['available', 'all', 'none', 'one', 'arranged']),
    volunteerId: z.string().trim().max(40).optional(),
    /** Eli, 28 Oct 2026 (D-1028-COMPANION): `arranged` — a volunteer already agreed; the save is the Confirm. Off by default: the volunteer's own email. */
    sendVolunteerDetails: z.boolean().optional().default(false),
    /** `arranged` only: "extra one-on-one training done for this match" for a tenant who uses a wheelchair (as Confirm asks). */
    wheelchairTraining: z.boolean().optional().default(false),
    /** Eli, 17 Oct 2026: how they get there (null for anything in the building). */
    transport: z.enum(TRANSPORT_KINDS).nullable().optional().default(null),
    /** 1.4.0 §2 — Companion one-to-one only: who else is coming. */
    second: SecondPersonSchema.default({ kind: 'none' }),
    /** "Anyone else?" Yes → saved as Needs the Director's OK; nothing goes to volunteers until an administrator approves. */
    anyoneElse: z.boolean().default(false),
  })
  .superRefine((r, ctx) => {
    if (CATEGORIES[r.category].destination === 'required' && !r.destination) ctx.addIssue({ code: 'custom', path: ['destination'], message: 'required' });
    if (r.moreStops.length && !r.destination) ctx.addIssue({ code: 'custom', path: ['moreStops'], message: 'first-stop-first' });
    if (r.category === 'trip' && !r.trip) ctx.addIssue({ code: 'custom', path: ['trip'], message: 'required' });
    if (r.category === 'other' && !r.otherWhat) ctx.addIssue({ code: 'custom', path: ['otherWhat'], message: 'required' });
    if (CATEGORIES[r.category].program !== 'COMPANION' && (r.second.kind !== 'none' || r.anyoneElse)) ctx.addIssue({ code: 'custom', path: ['second'], message: 'companion-only' });
    if (r.anyoneElse && r.second.kind === 'none') ctx.addIssue({ code: 'custom', path: ['anyoneElse'], message: 'second-first' });
    if ((r.audience === 'one' || r.audience === 'arranged') && !r.volunteerId) ctx.addIssue({ code: 'custom', path: ['volunteerId'], message: 'required' });
    if (r.audience === 'arranged' && r.anyoneElse) ctx.addIssue({ code: 'custom', path: ['audience'], message: 'arranged-needs-ok' });
  });
export type RequestInput = z.input<typeof RequestSchema>;

export const PickerQuerySchema = z.object({ category: z.enum(CATEGORY_KEYS), dayKey, minutes: z.coerce.number().int().min(0).max(24 * 60) });

export const ClaimAnswerSchema = z.discriminatedUnion('answer', [
  z.object({ answer: z.literal('take') }),
  z.object({ answer: z.literal('yes') }),
  z.object({ answer: z.literal('propose'), dayKey, minutes }),
  z.object({ answer: z.literal('no') }),
]);

/** The 1:2 ratio is shown as a warning on the sheet and the outing; it never refuses an outing (1.4.0 §2). */
export const OutingSchema = z.object({
  name: text(120).min(1),
  dayKey,
  minutes,
  place: DestinationSchema,
  description: optText(2000),
  volunteersNeeded: z.number().int().min(1).max(200).nullable(),
  tenantCapacity: z.number().int().min(1).max(500),
  kind: z.enum(['CE', 'GWW']).default('CE'),
  // Eli, 17 Oct 2026: 'companion' — every Ready Companion & Wellness volunteer (the group outing from Book)
  audience: z.enum(['events', 'all', 'companion']),
  /** Eli, 17 Oct 2026: the tenants going (TenantIDs) — each is put on the outing as a staff-entered yes; more can be added or taken off on the event. */
  tenants: z.array(z.string().trim().min(1).max(80)).max(60).default([]),
  transport: z.enum(TRANSPORT_KINDS).nullable().optional().default(null),
  /** Build the outing without emailing volunteers yet (sent from the outing later). */
  send: z.boolean().default(true),
});
export type OutingInput = z.input<typeof OutingSchema>;

/* ---- the five completion questions (the volunteer sign-out form, decided 29 Sep) ---- */

const hhmm = z.string().regex(/^([01]?\d|2[0-3]):[0-5]\d$/, 'time');

export const CompletionSchema = z.object({
  backAt: hhmm,
  stops: optText(400),
  allOkay: z.enum(['yes', 'no']),
  incident: z.enum(['yes', 'no']),
  anythingElse: optText(1500),
});
export type CompletionInput = z.infer<typeof CompletionSchema>;

/**
 * 26 Oct 2026 (D-1026-VISIONS): "Didn't happen" on a Companion close-out. Reasons are the office's words; they map to
 * existing appointment choice values (No Show · Cancelled with an existing initiator/category) — nothing new on SharePoint.
 */
export const DIDNT_HAPPEN_REASONS = ['tenant-not-home', 'cancelled-on-the-day', 'volunteer-couldnt-come'] as const;
export type DidntHappenReason = (typeof DIDNT_HAPPEN_REASONS)[number];
export const DidntHappenSchema = z.object({ reason: z.enum(DIDNT_HAPPEN_REASONS), notes: optText(1500) });
export type DidntHappenInput = z.infer<typeof DidntHappenSchema>;

/**
 * 23 Oct 2026 (D-1023-READING): the Reading & Services close-out is the office's alone — one short form. Did they
 * meet, how long (minutes, when they met), notes. The volunteer is never sent the five questions.
 */
export const READING_MET = ['yes', 'no', 'cancelled'] as const;
export const ReadingCloseoutSchema = z
  .object({
    met: z.enum(READING_MET),
    minutes: z.number().int().min(0).max(600).optional(),
    notes: optText(1500),
  })
  .refine((v) => v.met !== 'yes' || (v.minutes !== undefined && v.minutes > 0), { path: ['minutes'], message: 'How long did they read?' });
export type ReadingCloseoutInput = z.infer<typeof ReadingCloseoutSchema>;

/** A Reading volunteer's one note after the thank-you email. */
export const ReadingNoteSchema = z.object({ note: z.string().trim().min(1, 'Please write a few words.').max(2000) });

/** 24 Oct 2026 (D-1023-READING, Eli's picks): the office's short answer to a reading volunteer's note. */
export const ReadingNoteReplySchema = z.object({ message: z.string().trim().min(1, 'Please write a few words.').max(1000) });
export type ReadingNoteReplyInput = z.infer<typeof ReadingNoteReplySchema>;

/* ---- the Companion incident report (Downloads/7-Companion-Forms/ABF_Companion_Incident_Report_Form.docx) ---- */

export const INCIDENT_TYPES = ['injury', 'medical', 'fall', 'property', 'safety', 'policy', 'other'] as const;
export const SEVERITIES = ['minor', 'moderate', 'severe'] as const;
const yn = z.enum(['yes', 'no']);

export const WitnessSchema = z.object({ name: text(80).min(1), phone: optText(40), email: optEmail, statement: optText(3000) });

export const CompanionIncidentSchema = z
  .object({
    // required (the insurance items)
    types: z.array(z.enum(INCIDENT_TYPES)).min(1),
    typeOther: optText(120),
    location: text(200).min(1),
    whatHappened: text(6000).min(10),
    injuries: yn,
    injuryDescription: optText(2000),
    severity: z.enum(['', ...SEVERITIES]).default(''),
    medical: yn,
    medicalWhere: optText(200),
    called911: yn,
    emergencyContacts: yn,
    emergencyContactsWho: optText(200),
    firstAid: yn,
    firstAidWhat: optText(500),
    officeNotified: yn,
    officeNotifiedWho: optText(120),
    tenantOkayNow: yn,
    tenantOkayNote: optText(500),
    endedEarly: yn,
    returnedAt: hhmm,
    typedName: text(120).min(2),
    // optional
    weather: optText(120),
    individuals: optText(500),
    staffInvolved: optText(200),
    witnesses: z.array(WitnessSchema).max(6).default([]),
    hazards: z.enum(['', 'yes', 'no']).default(''),
    hazardsExplain: optText(1000),
    negligence: z.enum(['', 'yes', 'no']).default(''),
    negligenceExplain: optText(1000),
    protocols: z.enum(['', 'yes', 'no']).default(''),
    protocolsExplain: optText(1000),
    propertyDamage: z.enum(['', 'yes', 'no']).default(''),
    propertyDamageWhat: optText(1000),
    followUp: z.enum(['', 'yes', 'no']).default(''),
    followUpWhat: optText(500),
    followUpOwner: optText(120),
  })
  .superRefine((v, ctx) => {
    const need = (cond: boolean, path: string) => cond && ctx.addIssue({ code: 'custom', path: [path], message: 'required' });
    need(v.types.includes('other') && !v.typeOther, 'typeOther');
    need(v.injuries === 'yes' && !v.injuryDescription, 'injuryDescription');
    need(v.injuries === 'yes' && !v.severity, 'severity');
    need(v.medical === 'yes' && !v.medicalWhere, 'medicalWhere');
    need(v.officeNotified === 'yes' && !v.officeNotifiedWho, 'officeNotifiedWho');
    need(v.followUp === 'yes' && !v.followUpOwner, 'followUpOwner');
  });
export type CompanionIncidentInput = z.input<typeof CompanionIncidentSchema>;
export type CompanionIncident = z.infer<typeof CompanionIncidentSchema>;

/** The required items, in the order the steps ask them (a test checks each one refuses a blank). */
export const INCIDENT_REQUIRED = ['types', 'location', 'whatHappened', 'injuries', 'medical', 'called911', 'emergencyContacts', 'firstAid', 'officeNotified', 'tenantOkayNow', 'endedEarly', 'returnedAt', 'typedName'] as const;

/* ------------------------------------------------------------------------------------------------
 * Hours — the monthly summary
 * ---------------------------------------------------------------------------------------------- */

export interface HoursEntry {
  volunteerId: string;
  volunteerName: string;
  program: string;
  dayKey: string;
  minutes: number;
  /** "form" = from the completion form; "scheduled" = the scheduled length (marked). */
  source: 'form' | 'scheduled';
}

export interface HoursSummaryRow {
  key: string;
  name: string;
  program: string;
  visits: number;
  minutes: number;
  scheduledOnly: number;
}

/** Per volunteer (and program) and per program, for one month (yyyyMM). */
export function hoursSummary(entries: readonly HoursEntry[], month: string): { byVolunteer: HoursSummaryRow[]; byProgram: HoursSummaryRow[] } {
  const inMonth = entries.filter((e) => e.dayKey.slice(0, 6) === month);
  const add = (map: Map<string, HoursSummaryRow>, key: string, name: string, program: string, e: HoursEntry) => {
    const r = map.get(key) ?? { key, name, program, visits: 0, minutes: 0, scheduledOnly: 0 };
    r.visits++;
    r.minutes += e.minutes;
    if (e.source === 'scheduled') r.scheduledOnly++;
    map.set(key, r);
  };
  const v = new Map<string, HoursSummaryRow>();
  const p = new Map<string, HoursSummaryRow>();
  for (const e of inMonth) {
    add(v, `${e.volunteerId}|${e.program}`, e.volunteerName, e.program, e);
    add(p, e.program, e.program, e.program, e);
  }
  const byName = (a: HoursSummaryRow, b: HoursSummaryRow) => a.name.localeCompare(b.name) || a.program.localeCompare(b.program);
  return { byVolunteer: [...v.values()].sort(byName), byProgram: [...p.values()].sort(byName) };
}

/** "1 hour 5 minutes" */
export function durationWords(min: number, lang: 'en' | 'es'): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const es = lang === 'es';
  const hs = h ? `${h} ${es ? (h === 1 ? 'hora' : 'horas') : h === 1 ? 'hour' : 'hours'}` : '';
  const ms = m ? `${m} ${es ? (m === 1 ? 'minuto' : 'minutos') : m === 1 ? 'minute' : 'minutes'}` : '';
  return [hs, ms].filter(Boolean).join(' ') || (es ? '0 minutos' : '0 minutes');
}

/** Minutes between a start (minutes after midnight) and "h:mm" back (a 12-hour "3:05" after a 2:00 start is 15:05). */
export function minutesBetween(startMinutes: number, backAt: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(backAt);
  if (!m) return null;
  let back = Number(m[1]) * 60 + Number(m[2]);
  while (back < startMinutes && back + 12 * 60 <= 24 * 60) back += 12 * 60;
  const d = back - startMinutes;
  return d > 0 && d <= 12 * 60 ? d : null;
}

export function minutesLabel(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')}${h < 12 ? ' am' : ' pm'}`;
}
